import RoleService from '@/services/RoleService'
import { DataTableFilter } from '@/types/DataTable'
import Role from '@/types/Role'
import { DateTime } from 'luxon'
import { computed, Ref, ref } from 'vue'

interface RoleFilter {
  label: string
  field: string
  type?: string
  initial?: string | null
  queryParams?: Record<string, string>
  icon?: string
}

export function useRoleFilter({
  label,
  field,
  initial = null,
  queryParams,
  icon = 'fa-regular fa-user-tie',
}: RoleFilter): DataTableFilter<string, Role> {
  const loading = ref(false)

  const expires = ref<DateTime>()

  const model: Ref<string | null> = ref(initial)

  const items: Ref<Role[]> = ref([])

  const ready = Promise.withResolvers<boolean>()

  const load = async () => {
    loading.value = true

    const service = new RoleService()

    const response = await service.index(1, new URLSearchParams(queryParams).toString(), 100)

    items.value = response.data.data.sort((a, b) => a.title.localeCompare(b.title))

    expires.value = DateTime.now().plus({ minutes: 5 })

    ready.resolve(true)

    loading.value = false
  }

  load()

  return {
    model,
    value: computed(() => model.value),
    items: computed(
      () => {
        if (expires.value !== undefined && DateTime.now() > expires.value) {
          load()
        }

        return items.value.map(i => ({ title: i.title, value: i.id }))
      },
    ),
    resource: computed(() => items.value.find(item => item.id === model.value)),
    label,
    field,
    type: 'multi-select',
    loading,
    ready: ready.promise,
    required: true,
    icon,
  }
}
